// src/ui/components/InstallPrompt.tsx
import { useEffect, useState } from "react";
import { colors } from "../design-system/colors";
import { fontFamily, typeScale } from "../design-system/typography";
import { space, radius, minTapTarget } from "../design-system/spacing";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "kinemalens_install_prompt_dismissed_at";
const DISMISS_COOLDOWN_MS = 1000 * 60 * 60 * 24 * 7; // 1週間は再表示しない

export default function InstallPrompt() {
  const [deferredEvent, setDeferredEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      const lastDismissed = Number(localStorage.getItem(DISMISS_KEY) ?? 0);
      if (Date.now() - lastDismissed < DISMISS_COOLDOWN_MS) return;
      setDeferredEvent(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  if (!visible || !deferredEvent) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    await deferredEvent.prompt();
    await deferredEvent.userChoice;
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label="ホーム画面に追加"
      style={{
        position: "fixed",
        left: space.md,
        right: space.md,
        bottom: space.md,
        zIndex: 50,
        background: colors.bgElevated,
        border: `1px solid ${colors.surfaceBorder}`,
        borderRadius: radius.lg,
        padding: space.md,
        display: "flex",
        alignItems: "center",
        gap: space.md,
        fontFamily: fontFamily.ui,
        boxShadow: "0 8px 30px rgba(0,0,0,0.4)",
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, color: colors.textPrimary, fontSize: typeScale.body.size }}>
          ホーム画面に追加
        </div>
        <div style={{ fontSize: typeScale.caption.size, color: colors.textSecondary }}>
          ストア不要、1タップで次回から即起動できます
        </div>
      </div>
      <button
        onClick={dismiss}
        style={{
          minHeight: minTapTarget,
          minWidth: minTapTarget,
          background: "transparent",
          border: "none",
          color: colors.textMuted,
          fontSize: typeScale.body.size,
        }}
        aria-label="閉じる"
      >
        後で
      </button>
      <button
        onClick={install}
        style={{
          minHeight: minTapTarget,
          padding: `0 ${space.md}`,
          borderRadius: radius.full,
          border: "none",
          background: colors.amber,
          color: colors.bg,
          fontWeight: 700,
          fontFamily: fontFamily.ui,
        }}
      >
        追加する
      </button>
    </div>
  );
}
